/**
 * Apps-side glue for tactics-hub Phase 3 annotation drafts.
 *
 * Mirrors the Phase 1 `applyTacticsDraft` pattern (in `@4cm/brain/tactics`)
 * but for the Phase 3 sub-types: zones, coachingPoints, constraints,
 * voiceovers. The Brain emits a `draft_tactics_annotation` payload; the
 * client commit path resolves the active layout, merges via this
 * helper, and writes through `saveTacticsLayout`.
 *
 * Pure functions only — no DOM / storage / network so the same code
 * works in vitest (no DOM required) and from the real browser commit
 * path.
 */
import type { TacticsLayout } from '@4cm/core/tactics';
import {
  applyAnnotationDraft as applyAnnotationDraftBrain,
  type TacticsAnnotationDraftInput,
} from '@4cm/brain/tactics/tacticsAnnotationTools';

/**
 * Wire-side annotation draft payload — what the Brain emits.
 * Re-exported here so callers in `apps/web2` don't have to reach into
 * the brain package's tactics subpath directly.
 */
export type TacticsAnnotationDraftPayload = TacticsAnnotationDraftInput;

/**
 * Merge an annotation draft into an existing layout. Wrapper around
 * the brain helper so the apps-side import surface is symmetric with
 * Phase 1's `applyTacticsDraft`.
 *
 * Behaviour summary (full detail in `@4cm/brain/tactics`):
 *  - Zones / coachingPoints / constraints APPEND with stable generated
 *    ids — existing entries are never overwritten.
 *  - Voice-overs collapse on keyframeId (later wins) because the
 *    schema has no separate id field and only one transcript per
 *    keyframe makes sense.
 *  - Coaching-point positions are clamped to the pitch.
 *  - Returns null when every sub-type is empty so the caller can
 *    surface "draft had nothing usable" instead of writing a no-op.
 */
export function applyAnnotationDraft(
  draft: TacticsAnnotationDraftPayload,
  existing: TacticsLayout,
): TacticsLayout | null {
  return applyAnnotationDraftBrain(draft, existing);
}
